import React, { useEffect, useState } from 'react'
import { DatePicker, Select, Button, Table, Spin, Tooltip, Space, message } from 'antd'
import moment from 'moment'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router'
import { Popover, Tag, Typography } from 'antd'

import Layout from '../../components/layout/Layout'
import axiosInstance from '../../utils/axiosInstance'

const { RangePicker, Avatar } = DatePicker
const { Text } = Typography

const timeZones = [
  {
    value: '-5',
    label: '-5',
  },
  {
    value: '-6',
    label: '-6',
  },
  {
    value: '+5',
    label: '+5',
  },
  {
    value: '+6',
    label: '+6',
  },
]

const ReadMoreText = ({ text, maxWords = 3 }) => {
  const [expanded, setExpanded] = React.useState(false)

  const words = text.split(' ')
  const isLong = words.length > maxWords

  return (
    <span>
      {expanded || !isLong ? text : words.slice(0, maxWords).join(' ') + '...'}
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            marginLeft: 4,
            border: 'none',
            background: 'transparent',
            color: '#007bff',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </button>
      )}
    </span>
  )
}

const nestedKeysWithTitles = [
  { key: 'mandatoryGoals', title: 'Mandatory Goals' },
  { key: 'optionalGoals', title: 'Optional Goals' },
  { key: 'mandatoryPriorities', title: 'Mandatory Priorities' },
  { key: 'optionalPriorities', title: 'Optional Priorities' },
  { key: 'mandatoryHabits', title: 'Mandatory Habits' },
  { key: 'optionalHabits', title: 'Optional Habits' },
  { key: 'mandatoryChallenges', title: 'Mandatory Challenges' },
  { key: 'optionalChallenges', title: 'Optional Challenges' },
]

const renderItemWithNested = (item) => {
  return (
    <>
      {/* Parent row */}
      <div
        key={item._id}
        style={{
          display: 'grid',
          gridTemplateColumns: columns.map((c) => c.width).join(' '),
          borderBottom: '1px solid #eee',
        }}
      >
        {columns.map((col, idx) => (
          <div key={idx} style={{ padding: 8 }}>
            {col.render(item)}
          </div>
        ))}
      </div>

      {/* Nested rows with titles */}
      {nestedKeysWithTitles.map(({ key, title }) => {
        if (!item[key] || item[key].length === 0) return null

        return (
          <div key={key} style={{ marginLeft: 20, marginTop: 8 }}>
            {/* Section title */}
            <p style={{ fontWeight: 'bold', marginBottom: 4 }}>{title}</p>

            {/* Render nested items */}
            {item[key].map((nestedItem) => (
              <div key={nestedItem._id}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: columns.map((c) => c.width).join(' '),
                    borderBottom: '1px solid #eee',
                    background: '#fafafa',
                    paddingLeft: 8,
                  }}
                >
                  {columns.map((col, idx) => (
                    <div key={idx} style={{ padding: 8 }}>
                      {col.render(nestedItem)}
                    </div>
                  ))}
                </div>

                {/* // ! No need for now */}
                {/* Recursive call for deeper nesting */}
                {/* {renderItemWithNested(nestedItem)} */}
              </div>
            ))}
          </div>
        )
      })}
    </>
  )
}

const columns = [
  {
    label: 'Title',
    width: '1fr',
    render: (item) => item.subTitle,
  },
  {
    label: 'Status',
    width: '120px',
    render: (item) => (!item.eventStatus ? '-' : item.eventStatus == 'complete' ? 'Complete' : 'Pending'),
  },
  {
    label: 'Points',
    width: '80px',
    render: (item) => item.points ?? '-',
  },
  {
    label: 'Category',
    width: '80px',
    render: (item) => item.category ?? '-',
  },
  {
    label: 'Reps',
    width: '80px',
    render: (item) => item.eventReps ?? '-',
  },
  {
    label: 'Focus Area',
    width: '80px',
    render: (item) => item.parentTitle ?? '-',
  },
  {
    label: 'Rating',
    width: '80px',
    render: (item) => item.ratingFive ?? '-',
  },
  {
    label: 'Completion',
    width: '90px',
    render: (item) => (item.eventStatus == 'complete' && item.eventUpdatedAt ? moment(new Date(item.eventUpdatedAt)).format('HH:mm, MM-DD-YYYY') : '-'),
  },
  // ! Remove old version below soon...
  // {
  //   label: 'Prove it',
  //   width: '100px',
  //   render: (item) => {
  //     const text = item.proveItText || '-'
  //     if (text === '-') return '-'
  //     return <ReadMoreText text={text} maxWords={2} />
  //   },
  // },
  {
    label: 'Details',
    width: '120px', //
    render: (item) => <DetailsCell item={item} />,
  },
]

const renderLevelDetails = (level) => {
  const sections = [
    { title: 'Mandatory Goals', key: 'mandatoryGoals' },
    { title: 'Optional Goals', key: 'optionalGoals' },
    { title: 'Mandatory Priorities', key: 'mandatoryPriorities' },
    { title: 'Optional Priorities', key: 'optionalPriorities' },
    { title: 'Mandatory Habits', key: 'mandatoryHabits' },
    { title: 'Optional Habits', key: 'optionalHabits' },
    { title: 'Mandatory Challenges', key: 'mandatoryChallenges' },
    { title: 'Optional Challenges', key: 'optionalChallenges' },
  ]

  const gridTemplateColumns = columns.map((c) => c.width).join(' ')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {sections.map(({ title, key }) => {
        const items = level[key]
        if (!items || items.length === 0) return null

        return (
          <div key={key}>
            <p style={{ fontWeight: 'bold', marginBottom: 8 }}>{title}</p>

            <div
              style={{
                border: '1px solid #ddd',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns,
                  background: '#f5f5f5',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #ddd',
                }}
              >
                {columns.map((col) => (
                  <div key={col.label} style={{ padding: 8 }}>
                    {col.label}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {items.map((item) => renderItemWithNested(item))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const DetailsCell = ({ item }) => {
  const attachments = item.uploadedResponses || []
  const city = item?.location?.address?.[0]?.city
  const proveText = item?.proveItText
  const dynamicText = item?.dynamicText
  const dynamicTextFor = item?.dynamicTextFor

  if (attachments.length === 0 && !city && !proveText && !dynamicText) {
    return '-'
  }

  const popoverContent = (
    <Space direction="vertical" size={6}>
      {attachments.length > 0 && (
        <div>
          <strong>Attachments:</strong>
          {attachments.map((obj, i) => (
            <div key={i}>
              <a href={obj.mediaLink} target="_blank" rel="noreferrer">
                Download {i + 1}
              </a>
            </div>
          ))}
        </div>
      )}

      {city && (
        <div>
          <strong>Location:</strong> {city}
        </div>
      )}

      {proveText && (
        <div>
          <strong>Prove it text:</strong> {proveText}
        </div>
      )}

      {dynamicTextFor && (
        <div>
          <strong>{dynamicTextFor}:</strong> {dynamicText}
        </div>
      )}
    </Space>
  )

  return (
    <Popover content={popoverContent} title="Details" trigger="hover">
      <Space direction="vertical" size={2}>
        {attachments.length > 0 && <Tag color="blue">📎 {attachments.length}</Tag>}

        {city && (
          <Text ellipsis style={{ maxWidth: 120 }}>
            📍 {city}
          </Text>
        )}

        {proveText && (
          <Text ellipsis style={{ maxWidth: 120 }}>
            📝 {proveText}
          </Text>
        )}

        {dynamicText && (
          <Text ellipsis style={{ maxWidth: 120 }}>
            📂 {dynamicText}
          </Text>
        )}
      </Space>
    </Popover>
  )
}

function UserStacks() {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [selectedOrg, setSelectedOrg] = useState('')
  const [orgMembers, setoOrgMembers] = useState([])
  const [selectedMember, setSelectedMember] = useState('')
  const [data, setData] = useState(null)
  const [calculations, setCalculations] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [timeZone, setTimeZone] = useState(timeZones[0].value)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedLevelIndex, setSelectedLevelIndex] = useState(null)
  const [hoveredIndex, setHoveredIndex] = useState(null)

  // to find selected item on fetched data
  const selectedItem = data?.find((el) => el._id === selectedId)

  const getMyInfo = async () => {
    try {
      setLoading(true)

      const response = await axiosInstance.get(`/api/v1/admin/me`)

      // Check the admin organizations
      if (response?.data?.user?.organizations && response?.data?.user?.organizations.length > 0) {
        const org = response.data.user.organizations
        // console.log(org)

        // store to render at JSX
        org.forEach((element, idx) => {
          if (idx == 0) setSelectedOrg(element._id) // auto selecting the first organization to fetch some results
          setOrganizations((prevElements) => [
            ...prevElements,
            {
              value: element._id,
              label: element.name,
            },
          ])
        })
      }

      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
    }
  }

  const getData = async (id) => {
    try {
      // console.log(id)

      if (!startDate || !endDate || !id) {
        console.log(startDate, endDate)
        message.error('Please properly add start & end date! Along with user selection.')
        return
      }

      setLoading(true)

      const payload = {
        timeZone,
        startDate,
        endDate,
        // summaryOfClub: selectedOrg, // ! Remove soon
        userId: id,
      }

      // console.log(payload)

      const response = await axiosInstance.post(`/api/v1/metrics/stackTakenData`, payload)

      console.log(response.data)

      setData(response.data.mainData)

      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
    }
  }

  const getAClubMembers = async (id) => {
    try {
      setLoading(true)

      setoOrgMembers([])

      const response = await axiosInstance.get(`/api/v1/admin/getAClubMembers?clubId=${id}`)

      response.data.users.forEach((element) => {
        setoOrgMembers((prevElements) => [
          ...prevElements,
          {
            value: element._id,
            label: element.firstName + ' ' + element.lastName,
          },
        ])
      })

      setLoading(false)
    } catch (e) {
      console.log(e)
      setLoading(false)
    }
  }

  const handleRangePicker = (value) => {
    // console.log('Selected Time: ', value)
    value?.map((el, idx) => {
      // console.log(moment(new Date(el)).format('MMMM DD, YYYY h:mm:ss a')) // Readable format
      // console.log(new Date(el).getTime()) // Epoch timestamp in miliseconds
      let start = 0
      let end = 0
      if (idx == 0) {
        start = new Date(el).getTime()
        setStartDate(start)
      } else if (idx == 1) {
        end = new Date(el).getTime()
        setEndDate(end)
      }
      console.log(start)
      console.log(end)
    })
  }

  useEffect(() => {
    getMyInfo()
  }, [])

  useEffect(() => {
    if (selectedOrg) {
      getAClubMembers(selectedOrg)
    }
  }, [selectedOrg])

  useEffect(() => {
    // console.log('selectedMember')
    // console.log(selectedMember)
    if (selectedMember) {
      if (selectedMember) {
        getData(selectedMember)
      }
    }
  }, [selectedMember])

  if (organizations.length == 0) return null

  return (
    <Layout>
      <h1>Leader's Stack</h1>

      <Space>
        <Select
          placeholder="Please select club or business"
          style={{ width: 250 }}
          defaultValue={organizations[0].value}
          onChange={(value) => {
            setSelectedOrg(value)
            setData(null)
          }}
          options={organizations}
        />
        <RangePicker onChange={handleRangePicker} />

        {orgMembers.length && (
          <Select
            placeholder="Select a member (Mandatory)"
            style={{ width: 250 }}
            onChange={(value) => {
              console.log(value)
              setData(null)
              setSelectedMember(value)
            }}
            options={orgMembers}
            showSearch
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            allowClear
          />
        )}
        <Select placeholder="Please select timezone" style={{ width: 100 }} defaultValue={timeZones[0].value} onChange={(value) => setTimeZone(value)} options={timeZones} />

        <Button
          type="primary"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            getData(selectedMember)
          }}
          loading={loading}
        >
          Get data
        </Button>
      </Space>

      <br />
      <br />
      <div style={{ marginTop: 10 }}></div>
      <p style={{ color: '#c4c4c4' }}>Please change user to get expected data.</p>

      {selectedItem ? (
        <div>
          <div style={{}}>
            <p>Started at: {moment(new Date(selectedItem.createdAt)).format('HH:mm, MM-DD-YYYY')}</p>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <a
                target="_blank"
                href={`/createStack?id=${selectedItem.mainStack.focusAreaId}&organization=${selectedItem.mainStack.organization[0]}&stackId=${selectedItem.mainStack._id}`}
              >
                <p style={{ fontWeight: 'bold' }}>{selectedItem.mainStack.stackTitle}</p>
              </a>

              <button
                onClick={() => {
                  setSelectedId(null)
                  setSelectedLevelIndex(null)
                }}
                style={{
                  cursor: 'pointer',
                  padding: '6px 14px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#374151',
                  backgroundColor: '#F3F4F6',
                  border: '1px solid #D1D5DB',
                  borderRadius: 6,
                  transition: 'all 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#E5E7EB'
                  e.currentTarget.style.borderColor = '#9CA3AF'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6'
                  e.currentTarget.style.borderColor = '#D1D5DB'
                }}
              >
                Close this stack{' '}
                <span
                  style={{
                    display: 'inline-block',
                    fontWeight: 700,
                    fontSize: 16,
                    color: '#EF4444',
                    lineHeight: 1,
                  }}
                >
                  ×
                </span>
              </button>
            </div>
          </div>

          {selectedLevelIndex !== null ? (
            <div>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                }}
              >
                <p style={{ fontWeight: 'bold' }}>{selectedItem.levels[selectedLevelIndex].levelName}</p>

                <button
                  onClick={() => setSelectedLevelIndex(null)}
                  style={{
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#374151',
                    backgroundColor: '#F3F4F6',
                    border: '1px solid #D1D5DB',
                    borderRadius: 6,
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#E5E7EB'
                    e.currentTarget.style.borderColor = '#9CA3AF'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#F3F4F6'
                    e.currentTarget.style.borderColor = '#D1D5DB'
                  }}
                >
                  Close this level{' '}
                  <span
                    style={{
                      display: 'inline-block',
                      fontWeight: 700,
                      fontSize: 16,
                      color: '#EF4444',
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </span>
                </button>
              </div>

              {renderLevelDetails(selectedItem.levels[selectedLevelIndex])}
            </div>
          ) : (
            <div style={{}}>
              <p>Choose a level: </p>
              {selectedItem.levels.map((level, idx) => (
                <div
                  key={idx}
                  style={{
                    border: '1px solid #ddd',
                    padding: 10,
                    borderRadius: 6,
                    cursor: 'pointer',
                    marginBottom: 5,
                    backgroundColor: hoveredIndex === idx ? '#e0f0ff' : 'white',
                    transition: 'background-color 0.3s ease',
                  }}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setSelectedLevelIndex(idx)}
                >
                  {level.levelName}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        data && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 16,
              justifyContent: 'flex-start',
            }}
          >
            {data.map((el) => (
              <div
                key={el._id}
                style={{
                  border: '1px solid #ddd',
                  padding: 12,
                  paddingLeft: 12,
                  paddingRight: 40,
                  borderRadius: 8,
                }}
              >
                <p
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    margin: '4px 0',
                    lineHeight: 1.4,
                  }}
                >
                  {el.mainStack.stackTitle}
                </p>

                <button
                  onClick={() => setSelectedId(el._id)}
                  style={{
                    marginTop: 8,
                    padding: '8px 16px',
                    cursor: 'pointer',
                    backgroundColor: '#3B82F6',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    fontSize: 12,
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#2563EB')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#3B82F6')}
                >
                  Open
                </button>
              </div>
            ))}
          </div>
        )
      )}
    </Layout>
  )
}

export default UserStacks
